/* eslint-disable complexity, max-lines */
import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  fetch_configured_onboarding_entity_types,
  fetch_onboarding_entity_types,
  fetch_queue_onboarding_cases
} from '@/services/onboarding_journey_service';
import {
  GENERATE_ONBOARDING_REPORT_URL,
  GET_COMPANIES,
  GET_ONBOARDING_REPORT_AUDIT_TRAIL_URL,
  GET_ONBOARDING_REPORT_CONFIGURATION_URL,
  GET_ONBOARDING_REPORT_HISTORY_URL,
  GET_ONBOARDING_REPORT_LOOKUPS_URL,
  LIST_ENTITY_PROFILES_API_URL,
  ONBOARDING_REPORT_DATA_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';

const REPORT_OUTPUT_FORMATS = ['xlsx', 'pdf'];
const REPORT_CONFIGURATION_STORAGE_KEY = 'probaworkflows.onboardingReportConfiguration.v1';
const PREVIEW_PAGE_SIZE = 25;
const HISTORY_PAGE_SIZE = 7;
const DISALLOWED_OBJECT_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
const ALLOWED_REPORT_URL_PROTOCOLS = new Set(['http:', 'https:']);

const STATIC_FIELD_LABELS = new Map([
  ['client_name', 'Candidate / company'],
  ['city', 'City'],
  ['date_opened', 'Date opened'],
  ['age', 'Age'],
  ['case_number', 'Onboarding ref'],
  ['ethnicity', 'Ethnicity'],
  ['gender', 'Gender'],
  ['province', 'Source'],
  ['category_name', 'Category'],
  ['status_name', 'Status'],
  ['type', 'Journey'],
  ['created_by', 'Created by'],
  ['type_name', 'Onboarding type'],
  ['case_responses', 'Responses'],
  ['sub_category', 'Sub-category'],
  ['date_assigned', 'Date assigned'],
  ['assigned_attorney', 'Assigned owner'],
  ['journal_descriptions', 'Activity notes']
]);

function parse_api_data(response) {
  const raw = response?.data ?? response;
  if (typeof raw === 'string') return JSON.parse(raw);
  const parsed_data = raw || {};
  return parsed_data;
}

function safe_array(value) {
  const array_value = Array.isArray(value) ? value : [];
  return array_value;
}

function to_number(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function read_session_project_id() {
  if (typeof window === 'undefined') return null;
  const session_project_id = [
    'project_id',
    'current_project_id',
    'selected_project_id',
    'projectId'
  ].map((key) => to_number(window.sessionStorage.getItem(key))).find(Boolean) || null;
  return session_project_id;
}

function project_option(project_id, name = '') {
  return {
    id: Number(project_id),
    name: name || `Project ${project_id}`
  };
}

function first_present_value(values = [], fallback = '') {
  const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '');
  return value === undefined ? fallback : value;
}

function normalize_match_value(value = '') {
  return String(value || '').trim().toLowerCase();
}

function to_safe_object_key(value) {
  const normalized_key = String(value || '').trim();
  if (!normalized_key || DISALLOWED_OBJECT_KEYS.has(normalized_key)) return '';
  return normalized_key;
}

function read_safe_value(record, key, fallback = '') {
  const safe_key = to_safe_object_key(key);
  if (!safe_key || !record || typeof record !== 'object') return fallback;
  if (!Object.hasOwn(record, safe_key)) return fallback;
  return Reflect.get(record, safe_key);
}

function get_safe_report_url(url = '') {
  const normalized_url = String(url || '').trim();
  if (!normalized_url) return '';

  try {
    const base_origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed_url = new URL(normalized_url, base_origin);
    if (!ALLOWED_REPORT_URL_PROTOCOLS.has(parsed_url.protocol)) return '';
    return parsed_url.href;
  } catch {
    return '';
  }
}

function encode_html_text(value = '') {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function looks_like_html_response(value = '') {
  const normalized_value = String(value || '').trim().toLowerCase();
  if (!normalized_value) return false;
  return /^<(?:!doctype|html|body|head|script|style|iframe|div|span|p|br)\b/.test(normalized_value);
}

function project_id_from_record(record = {}) {
  return to_number(first_present_value([
    record?.project_id,
    record?.onboarding_project_id,
    record?.project?.id,
    record?.profile?.project_id,
    record?.profile?.project?.id,
    record?.entity?.project_id,
    record?.current_entity?.project_id
  ], null));
}

function project_name_from_record(record = {}) {
  return first_present_value([
    record?.project_name,
    record?.onboarding_project_name,
    record?.project?.project_name,
    record?.project?.name,
    record?.profile?.project_name,
    record?.profile?.project?.project_name,
    record?.profile?.project?.name
  ], '');
}

function unique_project_options_from_cases(cases = []) {
  const project_map = new Map();
  safe_array(cases).forEach((case_item) => {
    const project_id = project_id_from_record(case_item);
    if (!project_id || project_map.has(String(project_id))) return;
    project_map.set(String(project_id), project_option(project_id, project_name_from_record(case_item)));
  });
  return Array.from(project_map.values());
}

function case_matches_journey_option(case_item = {}, journey = {}) {
  const journey_id = String(journey?.id || '').trim();
  if (!journey_id) return true;

  const id_candidates = [
    case_item?.entity_type_id,
    case_item?.threshold_entity_type_id_used,
    case_item?.journey_tone,
    case_item?.journey_key
  ].map((value) => String(value || '').trim()).filter(Boolean);
  if (id_candidates.includes(journey_id)) return true;

  const journey_label = normalize_match_value(journey?.name);
  if (!journey_label) return false;

  const label_candidates = [
    case_item?.journey,
    case_item?.entity_type_name,
    case_item?.type,
    case_item?.type_name,
    case_item?.entity_type?.entity_type_name,
    case_item?.entity_type?.name
  ].map(normalize_match_value).filter(Boolean);
  return label_candidates.some((value) => value === journey_label || value.includes(journey_label));
}

function format_date_time(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function create_reports_state() {
  return {
    selected_project_id: '',
    available_projects: [],
    project_profile_counts: {},
    loading_projects: false,
    loading_journeys: false,
    loading_report_fields: false,
    checking_report: false,
    previewing_report: false,
    generating_report: false,
    loading_report_history: false,
    report_error: '',
    report_form: {
      start_date: '',
      end_date: '',
      journey: '',
      status: ''
    },
    onboarding_journeys: [],
    onboarding_report_cases: [],
    report_lookups: {
      fields: [],
      dynamic_fields: [],
      client_types: [],
      case_statuses: [],
      output_formats: []
    },
    selected_format: 'xlsx',
    selected_field_keys: [],
    included_field_keys: [],
    preview_rows: [],
    preview_fields: [],
    preview_count: 0,
    preview_page: 1,
    generated_history: [],
    generated_history_page: 1,
    generated_report_preview_open: false,
    generated_report_preview_title: '',
    generated_report_preview_type: 'excel',
    generated_report_preview_url: '',
    generated_report_preview_rows: [],
    generated_report_preview_fields: [],
    generated_report_preview_error: '',
    loading_generated_report_preview: false
  };
}

export default {
  name: 'OnboardingManageReports',
  mixins: [alert_mixin],
  data() {
    return create_reports_state();
  },
  computed: {
    project_id() {
      return to_number(
        this.selected_project_id
        || this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id()
      );
    },
    route_or_session_project_id() {
      return to_number(
        this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id()
      );
    },
    static_report_fields() {
      return safe_array(this.report_lookups.fields)
        .map((field) => ({
          key: field.key,
          label: STATIC_FIELD_LABELS.get(String(field.key)) || field.label || field.key,
          meta: 'Onboarding field',
          dynamic: false
        }))
        .filter((field) => field.key);
    },
    dynamic_report_fields() {
      return safe_array(this.report_lookups.dynamic_fields)
        .map((field) => ({
          key: field.key,
          section_question_id: field.section_question_id,
          question_id: field.question_id,
          label: field.label || field.key,
          meta: [field.form, field.section, field.question_type].filter(Boolean).join(' | '),
          dynamic: true
        }))
        .filter((field) => field.key);
    },
    all_report_fields() {
      return [...this.static_report_fields, ...this.dynamic_report_fields];
    },
    selected_fields() {
      const selected = new Set(this.selected_field_keys.map(String));
      return this.all_report_fields.filter((field) => selected.has(String(field.key)));
    },
    selected_field_count() {
      return this.selected_download_field_keys().length;
    },
    journey_options() {
      return safe_array(this.onboarding_journeys);
    },
    status_options() {
      return safe_array(this.report_lookups.case_statuses);
    },
    output_format_options() {
      const formats = safe_array(this.report_lookups.output_formats);
      const available = formats.length ? formats : REPORT_OUTPUT_FORMATS;
      return available.filter((format) => REPORT_OUTPUT_FORMATS.includes(format));
    },
    output_format_label() {
      return (format) => (format === 'xlsx' ? 'Excel' : String(format).toUpperCase());
    },
    report_columns() {
      const fields = this.preview_fields.length ? this.preview_fields : this.selected_download_field_keys();
      return fields.map((key) => ({ key, label: this.report_field_label(key) }));
    },
    preview_page_count() {
      return Math.max(1, Math.ceil(this.preview_count / PREVIEW_PAGE_SIZE));
    },
    bounded_preview_page() {
      return Math.min(Math.max(Number(this.preview_page) || 1, 1), this.preview_page_count);
    },
    visible_preview_rows() {
      const start = (this.bounded_preview_page - 1) * PREVIEW_PAGE_SIZE;
      return this.preview_rows.slice(start, start + PREVIEW_PAGE_SIZE);
    },
    preview_page_start() {
      if (!this.preview_count) return 0;
      return ((this.bounded_preview_page - 1) * PREVIEW_PAGE_SIZE) + 1;
    },
    preview_page_end() {
      return Math.min(this.bounded_preview_page * PREVIEW_PAGE_SIZE, this.preview_count);
    },
    preview_page_numbers() {
      return this.page_numbers(this.preview_page_count, this.bounded_preview_page);
    },
    generated_report_preview_columns() {
      return this.generated_report_preview_fields.map((key) => ({
        key,
        label: this.report_field_label(key)
      }));
    },
    history_page_count() {
      return Math.max(1, Math.ceil(this.generated_history.length / HISTORY_PAGE_SIZE));
    },
    bounded_history_page() {
      return Math.min(Math.max(Number(this.generated_history_page) || 1, 1), this.history_page_count);
    },
    paginated_history_rows() {
      const start = (this.bounded_history_page - 1) * HISTORY_PAGE_SIZE;
      return this.generated_history.slice(start, start + HISTORY_PAGE_SIZE);
    },
    history_page_start() {
      if (!this.generated_history.length) return 0;
      return ((this.bounded_history_page - 1) * HISTORY_PAGE_SIZE) + 1;
    },
    history_page_end() {
      return Math.min(this.bounded_history_page * HISTORY_PAGE_SIZE, this.generated_history.length);
    },
    history_page_numbers() {
      return this.page_numbers(this.history_page_count, this.bounded_history_page);
    },
    total_loading() {
      return this.loading_projects || this.loading_journeys || this.loading_report_fields;
    },
    page_loading_message() {
      return 'Loading reports...';
    },
    page_loading_detail() {
      return 'Fetching the available onboarding report fields.';
    }
  },
  mounted() {
    this.bootstrap_reports();
  },
  watch: {
    project_id(new_value, old_value) {
      if (!new_value || String(new_value) === String(old_value)) return;
      this.reset_report_results();
      this.load_report_screen();
    },
    'report_form.journey'(new_value, old_value) {
      if (String(new_value || '') === String(old_value || '')) return;
      this.reset_report_results();
      this.resolve_report_project_for_selected_journey();
    }
  },
  methods: {
    async bootstrap_reports() {
      await this.fetch_projects();
      await this.load_report_screen();
    },
    async fetch_projects() {
      this.loading_projects = true;
      try {
        const response = await axios.get(GET_COMPANIES, get_request_config());
        const parsed = parse_api_data(response);
        const items = safe_array(parsed.data || parsed.projects || parsed.results);
        this.available_projects = items
          .map((project) => project_option(
            project.id,
            project.name || project.project_name || project.title
          ))
          .filter((project) => project.id);
        if (!this.available_projects.length) {
          await this.resolve_projects_from_onboarding_cases();
        }
        this.resolve_report_project_context();
        await this.load_project_profile_counts();
        this.resolve_report_project_with_data();
      } catch (error) {
        this.available_projects = [];
        this.project_profile_counts = {};
        await this.resolve_projects_from_onboarding_cases();
        this.resolve_report_project_context();
        this.report_error = this.project_id
          ? ''
          : this.report_error_message(error, 'Failed to load your project context.');
      } finally {
        this.loading_projects = false;
      }
    },
    async resolve_projects_from_onboarding_cases() {
      const cases = await fetch_queue_onboarding_cases().catch(() => []);
      this.available_projects = unique_project_options_from_cases(cases);
    },
    resolve_report_project_context() {
      const preferred_project_id = this.route_or_session_project_id;
      if (preferred_project_id) {
        const matching_project = this.available_projects.find(
          (project) => String(project.id) === String(preferred_project_id)
        );
        if (!matching_project && !this.available_projects.length) {
          this.available_projects = [project_option(preferred_project_id)];
        }
        if (matching_project || !this.available_projects.length) {
          this.selected_project_id = String(preferred_project_id);
          return;
        }
      }
      if (!this.selected_project_id && this.available_projects.length) {
        this.selected_project_id = String(this.available_projects[0].id);
      }
    },
    async load_project_profile_counts() {
      const counts = await Promise.all(
        this.available_projects.map(async (project) => [
          String(project.id),
          await this.fetch_project_profile_count(project.id)
        ])
      );
      this.project_profile_counts = Object.fromEntries(counts);
    },
    async fetch_project_profile_count(project_id) {
      const response = await axios.get(
        LIST_ENTITY_PROFILES_API_URL,
        get_request_config({ params: { project_id, page: 1, page_size: 1 } })
      ).catch(() => null);

      if (!response) return null;
      const parsed = parse_api_data(response);
      return Number(parsed.pagination?.total_count || safe_array(parsed.data).length || 0);
    },
    resolve_report_project_with_data() {
      if (!this.available_projects.length || !this.selected_project_id) return;
      if (this.project_reportable_count(this.selected_project_id) !== 0) return;
      const project_with_data = this.available_projects.find(
        (project) => this.project_reportable_count(project.id) > 0
      );
      if (project_with_data) this.selected_project_id = String(project_with_data.id);
    },
    project_reportable_count(project_id) {
      const count = this.project_profile_counts[String(project_id)];
      return Number.isFinite(Number(count)) ? Number(count) : null;
    },
    async load_report_screen() {
      await this.fetch_report_journeys();
      await Promise.all([
        this.fetch_report_lookups(),
        this.fetch_report_history()
      ]);
    },
    async fetch_report_journeys() {
      this.loading_journeys = true;
      try {
        const [journeys, cases] = await Promise.all([
          fetch_onboarding_entity_types().catch(() => fetch_configured_onboarding_entity_types()),
          fetch_queue_onboarding_cases().catch(() => [])
        ]);
        this.onboarding_journeys = this.normalize_journey_options(journeys);
        this.onboarding_report_cases = safe_array(cases);
        if (
          this.report_form.journey
          && !this.onboarding_journeys.some((journey) => String(journey.id) === String(this.report_form.journey))
        ) {
          this.report_form.journey = '';
        }
        this.resolve_report_project_for_selected_journey();
      } finally {
        this.loading_journeys = false;
      }
    },
    normalize_journey_options(journeys = []) {
      const journey_map = new Map();
      safe_array(journeys)
        .filter((journey) => (
          journey?.id
          && journey?.is_active !== false
          && !journey?.is_unconfigured
        ))
        .forEach((journey) => {
          const id = String(journey.id);
          if (journey_map.has(id)) return;
          journey_map.set(id, {
            id,
            name: first_present_value([
              journey.name,
              journey.entity_type_name,
              journey.label
            ], 'Journey')
          });
        });
      return Array.from(journey_map.values())
        .sort((left, right) => left.name.localeCompare(right.name));
    },
    selected_journey_option() {
      if (!this.report_form.journey) return {};
      return this.onboarding_journeys.find(
        (journey) => String(journey.id) === String(this.report_form.journey)
      ) || {};
    },
    matching_report_cases() {
      const selected_journey = this.selected_journey_option();
      return this.onboarding_report_cases.filter((case_item) => (
        case_matches_journey_option(case_item, selected_journey)
      ));
    },
    report_project_scope_entries() {
      const project_counts = new Map();
      this.matching_report_cases().forEach((case_item) => {
        const project_id = project_id_from_record(case_item);
        if (!project_id) return;
        const key = String(project_id);
        project_counts.set(key, (project_counts.get(key) || 0) + 1);
      });
      return Array.from(project_counts.entries())
        .map(([project_id, count]) => ({ project_id: Number(project_id), count }))
        .sort((left, right) => right.count - left.count);
    },
    report_project_ids() {
      const scoped_project_ids = this.report_project_scope_entries().map((entry) => entry.project_id);
      const known_project_ids = this.report_form.journey
        ? scoped_project_ids
        : [
          ...scoped_project_ids,
          ...this.available_projects.map((project) => Number(project.id)).filter((id) => id > 0)
        ];
      const project_ids = [...new Set(known_project_ids.map(Number).filter((id) => id > 0))];
      if (project_ids.length) return project_ids;
      return this.project_id ? [this.project_id] : [];
    },
    resolve_report_project_for_selected_journey() {
      const [primary_scope] = this.report_project_scope_entries();
      if (!primary_scope?.project_id) return;
      if (!this.available_projects.some((project) => String(project.id) === String(primary_scope.project_id))) {
        this.available_projects = [
          ...this.available_projects,
          project_option(primary_scope.project_id)
        ];
      }
      this.selected_project_id = String(primary_scope.project_id);
    },
    async fetch_report_lookups() {
      this.loading_report_fields = true;
      try {
        const params = this.project_id ? { project_id: this.project_id } : {};
        const response = await axios.get(
          GET_ONBOARDING_REPORT_LOOKUPS_URL,
          get_request_config({ params })
        );
        const parsed = parse_api_data(response);
        this.report_lookups = {
          ...this.report_lookups,
          ...(parsed.data || {})
        };
        if (!(await this.restore_saved_report_configuration())) {
          this.apply_default_report_fields();
        }
        if (!this.output_format_options.includes(this.selected_format)) {
          this.selected_format = this.output_format_options[0] || 'xlsx';
        }
        this.report_error = '';
      } catch (error) {
        this.report_error = this.report_error_message(error, 'Failed to load onboarding report fields.');
      } finally {
        this.loading_report_fields = false;
      }
    },
    apply_default_report_fields() {
      const available_keys = new Set(this.all_report_fields.map((field) => String(field.key)));
      const selected = this.selected_field_keys.filter((key) => available_keys.has(String(key)));
      if (selected.length) {
        const selected_key_set = new Set(selected.map(String));
        const included = this.included_field_keys.filter((key) => selected_key_set.has(String(key)));
        this.selected_field_keys = selected;
        this.included_field_keys = included.length ? included : selected;
        return;
      }

      const preferred = ['client_name', 'case_number', 'type', 'status_name', 'date_opened'];
      const preferred_keys = preferred.filter((key) => available_keys.has(key));
      const dynamic_keys = this.dynamic_report_fields.slice(0, 8).map((field) => field.key);
      this.selected_field_keys = [...preferred_keys, ...dynamic_keys].slice(0, 12);
      this.included_field_keys = [...this.selected_field_keys];
    },
    report_configuration_storage_key(project_id = this.project_id) {
      return `${REPORT_CONFIGURATION_STORAGE_KEY}.${project_id || 'default'}`;
    },
    apply_saved_report_configuration(saved = {}) {
      const available_keys = new Set(this.all_report_fields.map((field) => String(field.key)));
      const selected_keys = safe_array(saved.selected_field_keys)
        .filter((key) => available_keys.has(String(key)));
      this.selected_field_keys = selected_keys;
      this.included_field_keys = [...selected_keys];
      return Boolean(selected_keys.length);
    },
    async restore_saved_report_configuration() {
      if (!this.project_id) return false;
      const response = await axios.get(
        GET_ONBOARDING_REPORT_CONFIGURATION_URL,
        get_request_config({ params: { project_id: this.project_id } })
      ).catch(() => null);

      if (response) {
        const parsed = parse_api_data(response);
        const saved = parsed.configuration || parsed.data || {};
        if (saved && this.apply_saved_report_configuration(saved)) return true;
      }

      const raw = window.localStorage.getItem(this.report_configuration_storage_key());
      if (!raw) return false;
      return this.apply_saved_report_configuration(JSON.parse(raw));
    },
    persist_local_report_configuration() {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(
        this.report_configuration_storage_key(),
        JSON.stringify({
          selected_field_keys: this.selected_field_keys,
          project_id: this.project_id,
          saved_at: new Date().toISOString()
        })
      );
    },
    is_report_field_selected(key) {
      return this.included_field_keys.map(String).includes(String(key));
    },
    toggle_report_field(key) {
      if (this.is_report_field_selected(key)) {
        this.included_field_keys = this.included_field_keys.filter((field_key) => String(field_key) !== String(key));
      } else {
        this.included_field_keys = [...this.included_field_keys, key];
      }
      this.reset_preview();
    },
    selected_download_field_keys() {
      const configured_keys = new Set(this.selected_fields.map((field) => String(field.key)));
      return this.included_field_keys.filter((key) => configured_keys.has(String(key)));
    },
    compact_values(values) {
      const items = Array.isArray(values) ? values : [values];
      return items.map(String).filter(Boolean);
    },
    report_domain_values() {
      return this.compact_values(this.report_form.journey);
    },
    case_status_text(case_item = {}) {
      return normalize_match_value(first_present_value([
        case_item?.current_workflow_status,
        case_item?.workflow_display_stage,
        case_item?.status_name,
        case_item?.status?.status,
        case_item?.status?.name,
        case_item?.status,
        case_item?.stage,
        case_item?.detail,
        case_item?.activation
      ], ''));
    },
    case_date_value(case_item = {}) {
      const value = first_present_value([
        case_item?.date_created,
        case_item?.date_opened,
        case_item?.created_at,
        case_item?.application_date,
        case_item?.submitted_at
      ], '');
      if (!value) return '';
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
    },
    case_matches_status_filter(case_item = {}) {
      const status = String(this.report_form.status || '').trim();
      if (!status) return true;
      const status_option = this.status_options.find((option) => String(option.id) === status);
      const match_values = [
        status,
        status_option?.name
      ].map(normalize_match_value).filter(Boolean);
      const case_status = this.case_status_text(case_item);
      return match_values.some((value) => case_status === value || case_status.includes(value));
    },
    case_matches_date_filter(case_item = {}) {
      const case_date = this.case_date_value(case_item);
      if (!case_date) return true;
      if (this.report_form.start_date && case_date < this.report_form.start_date) return false;
      if (this.report_form.end_date && case_date > this.report_form.end_date) return false;
      return true;
    },
    filtered_database_report_cases() {
      return this.matching_report_cases().filter((case_item) => (
        this.case_matches_status_filter(case_item)
        && this.case_matches_date_filter(case_item)
      ));
    },
    database_report_value(case_item = {}, field_key = '') {
      const safe_field_key = to_safe_object_key(field_key);
      const values_by_field = {
        client_name: [
          case_item.client_name,
          case_item.candidate_name,
          case_item.company_name,
          case_item.entity_name,
          case_item.display_name,
          case_item.name
        ],
        case_number: [
          case_item.case_number,
          case_item.case_code,
          case_item.case_ref,
          case_item.application_code,
          case_item.application_id
        ],
        type: [case_item.journey, case_item.entity_type_name, case_item.type],
        type_name: [case_item.journey, case_item.entity_type_name, case_item.type_name],
        status_name: [this.case_status_text(case_item)],
        date_opened: [this.case_date_value(case_item)],
        date_assigned: [case_item.date_assigned, case_item.assigned_at],
        assigned_attorney: [case_item.assigned_owner, case_item.owner, case_item.assigned_to],
        created_by: [case_item.created_by],
        category_name: [case_item.category_name, case_item.category],
        sub_category: [case_item.sub_category],
        age: [case_item.age],
        city: [case_item.city],
        gender: [case_item.gender],
        ethnicity: [case_item.ethnicity],
        province: [case_item.province, case_item.source],
        case_responses: [case_item.case_responses],
        journal_descriptions: [case_item.journal_descriptions]
      };
      const report_value = first_present_value([
        read_safe_value(case_item, safe_field_key, ''),
        ...(Reflect.get(values_by_field, safe_field_key) || [])
      ], '');
      return report_value;
    },
    database_report_row(case_item = {}, fields = []) {
      return Object.fromEntries(
        fields.map((field) => [field, this.database_report_value(case_item, field)])
      );
    },
    database_report_rows() {
      const fields = this.selected_download_field_keys();
      return this.filtered_database_report_cases()
        .map((case_item) => this.database_report_row(case_item, fields));
    },
    report_payload(project_id = this.project_id) {
      const fields = this.selected_download_field_keys();
      const journey_values = this.report_domain_values();
      return {
        project_id: Number(project_id),
        applicationtype: journey_values,
        case_record_type: journey_values,
        case_status: this.compact_values(this.report_form.status),
        start_date: this.report_form.start_date || null,
        end_date: this.report_form.end_date || null,
        fields,
        report_fields: fields,
        dynamic_filters: [],
        output_format: this.selected_format
      };
    },
    validate_report() {
      if (!this.project_id) {
        this.show_error('Unable to resolve the onboarding report context.');
        return false;
      }
      if (!this.selected_download_field_keys().length) {
        this.show_error('Select at least one field to include in the report.');
        return false;
      }
      if (!this.filtered_database_report_cases().length) {
        this.show_error('No onboarding data is available for the current report context.');
        return false;
      }
      return true;
    },
    is_no_report_data_error(error) {
      return Number(error?.response?.status) === 404;
    },
    async post_report_for_project(project_id) {
      const safe_url = get_safe_report_url(GENERATE_ONBOARDING_REPORT_URL);
      if (!safe_url) throw new Error('Invalid report endpoint URL.');
      try {
        const response = await axios.post(
          safe_url,
          this.report_payload(project_id),
          get_request_config()
        );
        return parse_api_data(response);
      } catch (error) {
        if (this.is_no_report_data_error(error)) {
          return { count: 0, data: [], fields: this.selected_download_field_keys() };
        }
        throw error;
      }
    },
    async check_report() {
      if (!this.validate_report()) return;
      this.checking_report = true;
      try {
        const count = this.filtered_database_report_cases().length;
        this.show_success(`${count} matching onboarding(s) found.`);
      } catch (error) {
        this.show_error(this.report_error_message(error, 'No onboarding report data found for the selected filters.'));
      } finally {
        this.checking_report = false;
      }
    },
    async preview_report() {
      if (!this.validate_report()) return;
      this.previewing_report = true;
      try {
        this.preview_fields = this.selected_download_field_keys();
        this.preview_rows = this.database_report_rows();
        this.preview_count = this.preview_rows.length;
        this.preview_page = 1;
        this.show_success(`${this.preview_count} row(s) loaded.`);
      } catch (error) {
        this.reset_preview();
        this.show_error(this.report_error_message(error, 'No onboarding report data found for the selected filters.'));
      } finally {
        this.previewing_report = false;
      }
    },
    async generate_report() {
      if (!this.validate_report()) return;
      this.generating_report = true;
      const loader = await this.show_loader('Generating onboarding report...');
      try {
        const reports = await Promise.all(
          this.report_project_ids().map((project_id) => this.post_report_for_project(project_id))
        );
        reports
          .map((parsed) => parsed.report || parsed.data || {})
          .filter((report) => Object.keys(report).length)
          .forEach((report) => {
            this.download_generated_report(report);
          });
        this.show_success(
          reports.length === 1
            ? 'Onboarding report generated.'
            : `${reports.length} onboarding report files generated.`
        );
        await this.fetch_report_history();
      } catch (error) {
        this.show_error(this.report_error_message(error, 'Failed to generate onboarding report.'));
      } finally {
        await this.close_loader(loader);
        this.generating_report = false;
      }
    },
    async fetch_report_history() {
      if (!this.project_id) return;
      this.loading_report_history = true;
      const config = get_request_config({ params: { project_id: this.project_id } });
      try {
        const [history_response, audit_response] = await Promise.all([
          axios.get(GET_ONBOARDING_REPORT_HISTORY_URL, config),
          axios.get(GET_ONBOARDING_REPORT_AUDIT_TRAIL_URL, config)
        ]);
        const history = safe_array(parse_api_data(history_response).data);
        const audit = safe_array(parse_api_data(audit_response).data);
        this.generated_history = history.length ? history : audit;
        this.set_history_page(this.generated_history_page);
      } catch (error) {
        this.generated_history = [];
        this.generated_history_page = 1;
        this.show_error(this.report_error_message(error, 'Failed to load generated report history.'));
      } finally {
        this.loading_report_history = false;
      }
    },
    reset_report_results() {
      this.reset_preview();
      this.generated_history = [];
      this.generated_history_page = 1;
    },
    reset_preview() {
      this.preview_rows = [];
      this.preview_fields = [];
      this.preview_count = 0;
      this.preview_page = 1;
    },
    page_numbers(total, current) {
      const visible_count = Math.min(total, 5);
      let start = Math.max(1, current - 2);
      const end = Math.min(total, start + visible_count - 1);
      start = Math.max(1, end - visible_count + 1);
      return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    },
    set_preview_page(page) {
      const numeric_page = Number(page) || 1;
      this.preview_page = Math.min(Math.max(numeric_page, 1), this.preview_page_count);
    },
    set_history_page(page) {
      const numeric_page = Number(page) || 1;
      this.generated_history_page = Math.min(Math.max(numeric_page, 1), this.history_page_count);
    },
    report_field_label(key) {
      const field = this.all_report_fields.find((item) => String(item.key) === String(key));
      return field?.label || key;
    },
    row_value(row = {}, key = '') {
      const value = read_safe_value(row, key, '');
      if (value === undefined || value === null || value === '') return '-';
      return String(value);
    },
    download_generated_report(report = {}) {
      const url = get_safe_report_url(report.download_url || report.presigned_url || report.file_url);
      if (!url) {
        this.show_info('The report was generated but no file URL was returned.');
        return;
      }

      const frame = document.createElement('iframe');
      frame.src = url;
      frame.title = this.generated_report_title(report);
      frame.style.display = 'none';
      document.body.appendChild(frame);
      window.setTimeout(() => frame.remove(), 60000);
    },
    async open_saved_generated_report(report = {}) {
      const url = get_safe_report_url(report.presigned_url || report.file_url);
      if (!url) {
        this.show_info('The report was generated but no file URL was returned.');
        return;
      }

      if (this.is_excel_report(report, url)) {
        await this.open_generated_excel_preview(report);
        return;
      }

      if (this.is_pdf_report(report, url)) {
        this.open_generated_pdf_preview(report, url);
        return;
      }

      this.open_generated_report_url(url);
    },
    open_generated_report_url(url) {
      const safe_url = get_safe_report_url(url);
      if (!safe_url) {
        this.show_info('The report link is not valid.');
        return;
      }

      const opened = window.open(safe_url, '_blank');
      if (opened) {
        opened.opener = null;
        opened.focus();
        return;
      }

      const link = document.createElement('a');
      link.href = safe_url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    is_excel_report(report = {}, url = '') {
      return ['xls', 'xlsx'].includes(this.generated_report_extension(report, url));
    },
    is_pdf_report(report = {}, url = '') {
      return this.generated_report_extension(report, url) === 'pdf';
    },
    generated_report_extension(report = {}, url = '') {
      const output_format = report.output_format?.internal_name
        || report.output_format
        || report.details?.output_format
        || report.details?.filters?.output_format;
      if (typeof output_format === 'string' && output_format) return output_format.replace(/^\./, '').toLowerCase();
      try {
        const pathname = new URL(url).pathname;
        const extension = pathname.split('.').pop();
        return extension && extension !== pathname ? extension.toLowerCase() : '';
      } catch {
        return '';
      }
    },
    generated_report_format_label(report = {}) {
      const extension = this.generated_report_extension(
        report,
        report.presigned_url || report.file_url || report.download_url || ''
      );
      if (['xls', 'xlsx'].includes(extension)) return 'Excel';
      if (extension === 'pdf') return 'PDF';
      return extension ? extension.toUpperCase() : 'File';
    },
    generated_report_format_class(report = {}) {
      const label = this.generated_report_format_label(report).toLowerCase();
      return {
        'is-excel': label === 'excel',
        'is-pdf': label === 'pdf'
      };
    },
    async open_generated_excel_preview(report = {}) {
      this.generated_report_preview_open = true;
      this.generated_report_preview_title = this.generated_report_title(report);
      this.generated_report_preview_type = 'excel';
      this.generated_report_preview_url = '';
      this.generated_report_preview_rows = [];
      this.generated_report_preview_fields = [];
      this.generated_report_preview_error = '';
      this.loading_generated_report_preview = true;

      try {
        const response = await axios.post(
          ONBOARDING_REPORT_DATA_URL,
          this.generated_report_preview_payload(report),
          get_request_config()
        );
        const parsed = parse_api_data(response);
        this.generated_report_preview_rows = safe_array(parsed.data);
        this.generated_report_preview_fields = safe_array(parsed.fields);
      } catch (error) {
        this.generated_report_preview_error = this.report_error_message(
          error,
          'Failed to open the generated Excel report.'
        );
      } finally {
        this.loading_generated_report_preview = false;
      }
    },
    open_generated_pdf_preview(report = {}, url = '') {
      this.generated_report_preview_open = true;
      this.generated_report_preview_title = this.generated_report_title(report);
      this.generated_report_preview_type = 'pdf';
      this.generated_report_preview_url = url;
      this.generated_report_preview_rows = [];
      this.generated_report_preview_fields = [];
      this.generated_report_preview_error = '';
      this.loading_generated_report_preview = false;
    },
    close_generated_report_preview() {
      this.generated_report_preview_open = false;
      this.generated_report_preview_title = '';
      this.generated_report_preview_type = 'excel';
      this.generated_report_preview_url = '';
      this.generated_report_preview_rows = [];
      this.generated_report_preview_fields = [];
      this.generated_report_preview_error = '';
      this.loading_generated_report_preview = false;
    },
    generated_report_preview_payload(report = {}) {
      const filters = report.details?.filters || {};
      const fields = safe_array(report.details?.field_keys || filters.fields || filters.report_fields);
      const fallback_payload = this.report_payload();
      return {
        ...filters,
        project_id: filters.project_id || this.project_id,
        fields: fields.length ? fields : fallback_payload.fields,
        report_fields: fields.length ? fields : fallback_payload.report_fields,
        output_format: filters.output_format || 'xlsx'
      };
    },
    generated_report_title(report = {}) {
      return report.name || `Onboarding Report ${report.report_id || report.id || ''}`.trim();
    },
    generated_report_meta(report = {}) {
      return [
        report.downloaded_by || report.generated_by || 'Generated report',
        format_date_time(report.date || report.date_completed || report.date_created),
        `${report.details?.row_count ?? '-'} row(s)`
      ].filter(Boolean).join(' | ');
    },
    report_error_message(error, fallback = 'Failed to load onboarding reports.') {
      const data = error?.response?.data;
      if (!data) return error?.message || fallback;
      if (typeof data === 'string') {
        return looks_like_html_response(data) ? fallback : encode_html_text(data);
      }
      if (typeof data.message === 'string') return encode_html_text(data.message);
      if (typeof data.detail === 'string') return encode_html_text(data.detail);
      const first_key = Object.keys(data)[0];
      const first_value = first_key ? read_safe_value(data, first_key, '') : '';
      if (Array.isArray(first_value)) return encode_html_text(first_value.join(', '));
      if (typeof first_value === 'string') return encode_html_text(first_value);
      return error?.message || fallback;
    }
  }
};
